import { useEffect, useRef, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Image as ImageIcon, Upload, Trash2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { applyAccentColor, cacheAccentColor } from "@/lib/branding";
import { toast } from "sonner";
import { LinkedSignIns } from "@/components/LinkedSignIns";

const DEFAULT_ACCENT = "#8B1E23";

export default function Settings() {
  const [ministryName, setMinistryName] = useState("");
  const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT);
  const [hasLogo, setHasLogo] = useState(false);
  const [logoVersion, setLogoVersion] = useState(Date.now());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/branding").then(({ data }) => {
      setMinistryName(data.ministry_name || "");
      setAccentColor(data.accent_color || DEFAULT_ACCENT);
      setHasLogo(data.has_logo);
    });
  }, []);

  const saveBranding = async () => {
    setSaving(true);
    try {
      await api.put("/branding", { ministry_name: ministryName, accent_color: accentColor });
      applyAccentColor(accentColor);
      cacheAccentColor(accentColor);
      toast.success("Branding saved");
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      await api.post("/branding/logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setHasLogo(true);
      setLogoVersion(Date.now());
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeLogo = async () => {
    await api.delete("/branding/logo");
    setHasLogo(false);
    toast.success("Logo removed");
  };

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-10">
        <h1 className="font-sacred text-3xl text-[var(--brand-navy)] dark:text-amber-100 mb-1">Branding Settings</h1>
        <p className="text-sm text-muted-foreground mb-8">Your ministry name and logo will appear on exported PDFs and PowerPoint slides</p>

        <div className="bg-white dark:bg-[var(--brand-card-dark)] border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-lg p-6 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="ministry-name">Ministry name</Label>
            <div className="flex gap-2">
              <Input id="ministry-name" placeholder="e.g. St. Michael Prayer Group" value={ministryName} onChange={(e) => setMinistryName(e.target.value)} data-testid="ministry-name-input" />
              <Button onClick={saveBranding} disabled={saving} className="bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white shrink-0" data-testid="save-ministry-name-button">
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="accent-color">Ministry accent color</Label>
            <div className="flex items-center gap-3">
              <input
                id="accent-color"
                type="color"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                className="h-10 w-14 rounded border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] cursor-pointer bg-transparent"
                data-testid="accent-color-input"
              />
              <span className="font-mono text-sm text-muted-foreground" data-testid="accent-color-value">{accentColor}</span>
              <Button variant="outline" size="sm" onClick={saveBranding} disabled={saving} data-testid="save-accent-color-button">
                {saving ? "Saving..." : "Apply"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">This color is used for buttons and highlights across the app.</p>
          </div>

          <div className="space-y-2">
            <Label>Ministry logo</Label>
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-lg border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] flex items-center justify-center overflow-hidden bg-[#F5F3EE] dark:bg-[var(--brand-bg-dark)]" data-testid="logo-preview">
                {hasLogo ? (
                  <img src={`${API}/branding/logo?v=${logoVersion}`} alt="Ministry logo" className="h-full w-full object-contain" />
                ) : (
                  <ImageIcon className="h-8 w-8 text-muted-foreground/40" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-logo-button">
                  <Upload className="h-4 w-4 mr-2" /> {uploading ? "Uploading..." : hasLogo ? "Replace logo" : "Upload logo"}
                </Button>
                {hasLogo && (
                  <Button variant="ghost" className="text-[var(--brand-crimson)] justify-start" onClick={removeLogo} data-testid="remove-logo-button">
                    <Trash2 className="h-4 w-4 mr-2" /> Remove logo
                  </Button>
                )}
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleFileChange} data-testid="logo-file-input" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">PNG, JPEG, or WEBP. Max 3MB.</p>
          </div>
        </div>
        <LinkedSignIns />
      </div>
    </div>
  );
}
